<template>
  <div class="modal-container">
    <Dialog :visible="display" :modal="true" class="update-bank-details-modal" @update:visible="onClose">
      <template #header>
        <h1 class="title pm-txt-color">Update your bank account details</h1>
      </template>
      <div class="modal-content">
        <UnconditionalBankDetailsStep
          :value="bankDetails"
          :errors="bankDetailsErrors"
          :serviceAgreementUrl="serviceAgreementUrl"
          @update="onBankDetailsUpdate"
        >
          <template #description>
            <p class="summary-banner pm-txt-color">
              The bank details will be used to deposit your loan funds and to process your monthly interest repayments.
            </p>
          </template>
        </UnconditionalBankDetailsStep>
      </div>
      <template #footer>
        <div class="modal-footer">
          <p class="close" @click="onClose">Back</p>
          <Button :disabled="isLoading" label="Submit" type="primary" @click="onSubmit" />
        </div>
        <div v-if="errorMessage" class="mr-4 error-text">{{ errorMessage }}</div>
      </template>
    </Dialog>
  </div>
</template>

<script>
import { API, TECHLEND_BE_URL } from '@/constants.api';
import { _post } from '@/services/crudService';
import { fetchServiceAgreementUrl } from '@/services/serviceAgreement';
import { mapBankDetailsApiError, validateBankDetails } from '@/services/bankDetailsValidation';
import { mapGetters } from 'vuex';
import Dialog from 'primevue/dialog';
import UnconditionalBankDetailsStep from './UnconditionalBankDetailsStep.vue';

export default {
  components: {
    Dialog,
    UnconditionalBankDetailsStep,
  },
  props: {
    display: { type: Boolean, default: false },
    task: { type: Object, default: null },
  },
  emits: ['completed', 'close'],
  data() {
    return {
      bankDetails: {
        bankAccountName: '',
        financialInstitutionName: '',
        bsb: '',
        accountNumber: '',
        directDebitAuthority: false,
        accountHolderAuthority: false,
      },
      bankDetailsErrors: {},
      errorMessage: '',
      isLoading: false,
      // Resolved when the dialog opens, so a re-collected mandate records the agreement version in
      // effect at that moment rather than one baked in at build time.
      serviceAgreementUrl: '',
    };
  },
  computed: {
    ...mapGetters({
      selectedLoanId: 'loans/selectedLoanId',
    }),
  },
  watch: {
    // Mounted for the life of the page and toggled via `display`, so the fetch hangs off the
    // transition to open rather than a lifecycle hook.
    display: {
      immediate: true,
      async handler(isOpen) {
        if (!isOpen) return;
        this.serviceAgreementUrl = await fetchServiceAgreementUrl(this.selectedLoanId);
      },
    },
  },
  methods: {
    onBankDetailsUpdate(updated) {
      this.bankDetails = updated;
      // Clearing on edit stops a message sitting under a field the customer has already corrected,
      // which reads as though the correction was rejected.
      this.bankDetailsErrors = {};
      this.errorMessage = '';
    },
    // Mirrors the payload the offer acceptance screen sends, so the same details captured through
    // either screen persist identically. The agreement URL records which version the customer
    // ticked against; it is omitted when the lookup gave us nothing, in which case the backend
    // stamps the currently effective version on the mandate rather than leaving the reference blank.
    buildPayload() {
      const payload = {
        applicationId: this.selectedLoanId,
        taskId: this.task?.id,
        bankAccountName: this.bankDetails.bankAccountName,
        financialInstitutionName: this.bankDetails.financialInstitutionName,
        bankBSBNumber: this.bankDetails.bsb,
        bankAccountNumber: this.bankDetails.accountNumber,
        accountHolderAuthority: this.bankDetails.accountHolderAuthority,
      };
      if (this.serviceAgreementUrl) payload.serviceAgreementUrl = this.serviceAgreementUrl;
      return payload;
    },
    onClose() {
      this.$emit('close');
    },
    async onSubmit() {
      this.errorMessage = '';
      this.bankDetailsErrors = validateBankDetails(this.bankDetails);
      if (Object.keys(this.bankDetailsErrors).length) {
        this.errorMessage = 'The form has missing or invalid fields. Please check before continuing.';
        return;
      }

      this.isLoading = true;
      try {
        await _post(TECHLEND_BE_URL + API.DIRECT_DEBIT.SETUP, this.buildPayload());
        this.$toast.add({
          severity: 'success',
          summary: 'Bank account updated',
          detail: 'Your bank account details have been updated successfully.',
          life: 4000,
        });
        this.$emit('completed');
      } catch (err) {
        const { fieldErrors, message } = mapBankDetailsApiError(err);
        this.bankDetailsErrors = fieldErrors;
        this.errorMessage = message;
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/global.scss';

.title {
  font-weight: bold;
  color: $primary-color;
  font-size: 2.25rem;
  @media (max-width: 992px) {
    font-size: 25px;
  }
}

.modal-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 2rem;
  margin-top: 2rem;

  .close {
    margin: 0;
    cursor: pointer;
  }
}

// A flat tint with no accent bar or icon, sitting flush with the field columns below it. Tinted
// from the shared sky blue rather than a fixed hex, so the white label builds keep one source for
// the colour: over white this lands within a few units of the value in the design.
.summary-banner {
  margin: 0 0 1.5rem;
  padding: 1rem;
  border-radius: 4px;
  background: rgba($sky-blue-color, 0.12);
  line-height: 1.5;
}

.error-text {
  color: red;
  font-size: 14px;
}

.modal-content {
  border: none;
}

:global(.update-bank-details-modal) {
  width: calc(100vw - 1rem);

  @media (min-width: 640px) {
    width: 80vw;
  }
}
</style>
